import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import {
  ClipboardList, Users, CalendarDays, Timer, AlertTriangle,
  Pause, MessageSquare, FileText, Banknote, TrendingUp,
  UserCheck, GraduationCap, LayoutDashboard, Plus, Trash2,
  Save, X, Download, RefreshCw, Search, FileBarChart2, Loader2, Calendar,
  CreditCard, Activity
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PALETTE = ['#f97316', '#0ea5e9', '#10b981', '#a855f7', '#ef4444', '#eab308', '#ec4899', '#14b8a6'];

// ---------- Tab definitions ----------
// type:  'collection'  → /api/ops-tracker/<slug>  (CRUD)
//        'analytics'   → /api/ops-tracker/analytics/<slug>  (read-only derived view)
const TABS = [
  {
    id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, type: 'dashboard',
    description: 'Live operational KPIs and trends across all sheets',
  },
  {
    id: 'daily-log', label: 'Daily Log', icon: ClipboardList, type: 'collection',
    description: 'Master log — one row per session. All other tabs roll up from this.',
    columns: [
      { key: 'date', label: 'Date', width: 90 },
      { key: 'student_name', label: 'Student' },
      { key: 'observer_name', label: 'Observer' },
      { key: 'session_status', label: 'Status', options: ['Completed', 'Missed', 'Postponed', 'Scheduled', 'Trial'] },
      { key: 'duration_mins', label: 'Duration (min)', type: 'number', width: 110 },
      { key: 'missed_by', label: 'Missed By' },
      { key: 'postponed_by', label: 'Postponed By' },
      { key: 'rescheduled_date', label: 'Rescheduled' },
      { key: 'makeup_completed', label: 'Make-up' },
      { key: 'notes', label: 'Notes' },
    ],
    chart: 'daily-log',
  },
  {
    id: 'subscribers', label: 'Subscribers', icon: Users, type: 'collection',
    description: 'Active subscriber list with revenue per month',
    columns: [
      { key: 'child_name', label: 'Child' },
      { key: 'start_date', label: 'Start Date' },
      { key: 'end_date', label: 'End Date' },
      { key: 'duration_months', label: 'Months', type: 'number', width: 90 },
      { key: 'total_fee_collected', label: 'Total Fee (₹)', type: 'number' },
      { key: 'feb_revenue', label: 'Feb Rev (₹)', type: 'number' },
      { key: 'mar_revenue', label: 'Mar Rev (₹)', type: 'number' },
      { key: 'apr_revenue', label: 'Apr Rev (₹)', type: 'number' },
      { key: 'observer_assigned', label: 'Observer' },
      { key: 'plan', label: 'Plan' },
    ],
    chart: 'subscribers',
  },
  {
    id: 'session-schedule', label: 'Session Schedule', icon: CalendarDays, type: 'collection',
    description: 'Monthly session plan per child',
    columns: [
      { key: 'child_name', label: 'Child' },
      { key: 'observer', label: 'Observer' },
      { key: 'sessions_feb', label: 'Sessions', type: 'number', width: 90 },
      { key: 'session_dates', label: 'Session Dates' },
      { key: 'day_pattern', label: 'Day Pattern' },
      { key: 'status', label: 'Status' },
    ],
    chart: 'session-schedule',
  },
  {
    id: 'session-duration', label: 'Session Duration', icon: Timer, type: 'collection',
    description: 'Scheduled vs actual minutes per session',
    columns: [
      { key: 'date', label: 'Date', width: 90 },
      { key: 'student', label: 'Student' },
      { key: 'observer', label: 'Observer' },
      { key: 'scheduled_duration', label: 'Scheduled (min)', type: 'number' },
      { key: 'actual_duration', label: 'Actual (min)', type: 'number' },
      { key: 'variance', label: 'Variance', type: 'number' },
      { key: 'session_status', label: 'Status' },
      { key: 'notes', label: 'Notes' },
    ],
    chart: 'session-duration',
  },
  {
    id: 'missed-sessions', label: 'Missed Sessions', icon: AlertTriangle, type: 'collection',
    description: 'Detailed log of missed sessions with reasons',
    columns: [
      { key: 'date', label: 'Date', width: 90 },
      { key: 'student', label: 'Student' },
      { key: 'observer', label: 'Observer' },
      { key: 'reason_student', label: 'Reason (Student)' },
      { key: 'reason_observer', label: 'Reason (Observer)' },
      { key: 'notified_by', label: 'Notified By' },
      { key: 'rescheduled_date', label: 'Rescheduled' },
      { key: 'status', label: 'Status', options: ['Open', 'Resolved', 'Rescheduled'] },
      { key: 'notes', label: 'Notes' },
    ],
    chart: 'missed-sessions',
  },
  {
    id: 'postponements', label: 'Postponements', icon: Pause, type: 'collection',
    description: 'Postponements with impact on observer pay',
    columns: [
      { key: 'original_date', label: 'Original Date' },
      { key: 'student', label: 'Student' },
      { key: 'observer', label: 'Observer' },
      { key: 'postponed_by', label: 'Postponed By', options: ['Observer', 'Student', 'School'] },
      { key: 'reason', label: 'Reason' },
      { key: 'new_date', label: 'New Date' },
      { key: 'makeup_completed', label: 'Make-up', options: ['Yes', 'No'] },
      { key: 'impact_on_pay', label: 'Pay Impact', options: ['Paid', 'Deducted', 'TBC'] },
      { key: 'notes', label: 'Notes' },
    ],
    chart: 'postponements',
  },
  {
    id: 'queries', label: 'Queries & Inquiries', icon: MessageSquare, type: 'collection',
    description: 'Support tickets, parent/observer inquiries with TAT',
    columns: [
      { key: 'date', label: 'Date', width: 100 },
      { key: 'from_name', label: 'From' },
      { key: 'role', label: 'Role', options: ['Parent', 'Observer', 'School'] },
      { key: 'channel', label: 'Channel', options: ['WhatsApp', 'Call', 'Email'] },
      { key: 'details', label: 'Details' },
      { key: 'assigned_to', label: 'Assigned' },
      { key: 'status', label: 'Status', options: ['Open', 'In Progress', 'Resolved', 'Closed'] },
      { key: 'resolved_on', label: 'Resolved' },
      { key: 'tat_hours', label: 'TAT (hrs)', type: 'number' },
      { key: 'resolution_notes', label: 'Resolution Notes' },
    ],
    chart: 'queries',
  },
  {
    id: 'parent-forms', label: 'Parent Form Responses', icon: FileText, type: 'collection',
    description: 'Parent intake form submissions',
    columns: [
      { key: 'submission_date', label: 'Submitted' },
      { key: 'student_name', label: 'Student' },
      { key: 'age', label: 'Age', width: 70 },
      { key: 'class', label: 'Class', width: 80 },
      { key: 'gender', label: 'Gender', width: 90 },
      { key: 'school', label: 'School' },
      { key: 'father', label: 'Father' },
      { key: 'mother', label: 'Mother' },
      { key: 'contact_numbers', label: 'Contacts' },
      { key: 'preferred_time', label: 'Pref Time' },
      { key: 'session_monitor', label: 'Monitor' },
    ],
    chart: 'parent-forms',
  },
  {
    id: 'observer-payroll', label: 'Observer Payroll', icon: Banknote, type: 'collection',
    description: 'Per-observer pay sheet for the month',
    columns: [
      { key: 'observer_name', label: 'Observer' },
      { key: 'children_assigned', label: 'Children Assigned' },
      { key: 'sessions_feb', label: 'Sessions', type: 'number' },
      { key: 'pay_per_session', label: 'Pay/Session (₹)', type: 'number' },
      { key: 'total_pay', label: 'Total Pay (₹)', type: 'number' },
      { key: 'paid_by', label: 'Paid By' },
      { key: 'notes', label: 'Notes' },
    ],
    chart: 'observer-payroll',
  },
  {
    id: 'vendor-costs', label: 'Vendor Costs', icon: CreditCard, type: 'collection',
    description: 'Monthly vendor and third-party service costs',
    columns: [
      { key: 'month', label: 'Month (YYYY-MM)' },
      { key: 'vendor_name', label: 'Vendor' },
      { key: 'purpose', label: 'Purpose' },
      { key: 'plan_usage', label: 'Plan / Usage' },
      { key: 'cost_current', label: 'Cost (Rs.)', type: 'number' },
      { key: 'cost_prev', label: 'Prev Month (Rs.)', type: 'number' },
      { key: 'invoice_no', label: 'Invoice No.' },
      { key: 'due_date', label: 'Due Date' },
      { key: 'payment_status', label: 'Payment', options: ['Paid', 'Pending', 'Auto-debit'] },
      { key: 'status', label: 'Status', options: ['Active', 'Inactive'] },
    ],
    chart: 'vendor-costs',
  },
  {
    id: 'platform-usage', label: 'Platform Usage', icon: Activity, type: 'collection',
    description: 'Monthly platform engagement and usage metrics',
    columns: [
      { key: 'month', label: 'Month (YYYY-MM)' },
      { key: 'metric', label: 'Metric' },
      { key: 'volume', label: 'Volume', type: 'number' },
      { key: 'unit_cost', label: 'Unit Cost (Rs.)', type: 'number' },
      { key: 'total_cost', label: 'Total Cost (Rs.)', type: 'number' },
      { key: 'vs_last_month', label: 'vs Last Month' },
      { key: 'notes', label: 'Notes' },
    ],
    chart: 'platform-usage',
  },
  // Auto-derived (read-only) tabs
  {
    id: 'observer-tracker', label: 'Observer Tracker', icon: UserCheck, type: 'analytics',
    description: 'Auto-calculated from Daily Log',
    endpoint: 'observer-tracker',
  },
  {
    id: 'student-tracker', label: 'Student Tracker', icon: GraduationCap, type: 'analytics',
    description: 'Auto-calculated from Daily Log',
    endpoint: 'student-tracker',
  },
  {
    id: 'pnl', label: 'P&L Summary', icon: TrendingUp, type: 'analytics',
    description: 'Auto-calculated revenue vs payout',
    endpoint: 'pnl',
  },
];

// ===================== Main Component =====================

export default function AdminOperationsTracker() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const tab = TABS.find(t => t.id === activeTab);

  return (
    <div className="space-y-4" data-testid="ops-tracker-root">
      {/* Tab strip */}
      <div className="bg-white border rounded-lg overflow-x-auto">
        <div className="flex min-w-max">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = t.id === activeTab;
            return (
              <button
                key={t.id}
                data-testid={`ops-tab-${t.id}`}
                onClick={() => setActiveTab(t.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors whitespace-nowrap ${
                  active
                    ? 'border-orange-500 text-orange-600 bg-orange-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab body */}
      <div data-testid={`ops-panel-${activeTab}`}>
        {tab?.type === 'dashboard' && <DashboardTab />}
        {tab?.type === 'collection' && <CollectionTab key={tab.id} tab={tab} />}
        {tab?.type === 'analytics' && <AnalyticsTab key={tab.id} tab={tab} />}
      </div>
    </div>
  );
}

// ===================== Dashboard tab =====================

const NAVY = [37, 75, 112];
const ORANGE = [240, 168, 32];
const LIGHT_NAVY = [230, 237, 245];

function downloadMonthlyPDF(report) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const ML = 15;
  const MR = W - 15;
  const CW = MR - ML;
  const CONTENT_TOP = 30;
  const CONTENT_BOT = H - 14;
  const LIGHT_BLUE = [230, 237, 245];

  const fmtRs = (n) => `Rs. ${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

  let pageNum = 1;
  let isCoverPage = true;
  let y = 0;

  const drawHeader = () => {
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 22, 'F');
    doc.setFillColor(...ORANGE);
    doc.rect(0, 22, W, 1.2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('SANJAYA', ML, 10);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Child Development Monitoring Platform', ML, 17);
    doc.setTextColor(...ORANGE);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('CONFIDENTIAL', MR, 10, { align: 'right' });
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.text(`Period: ${report.month_label}`, MR, 17, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  };

  const drawFooter = () => {
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.3);
    doc.line(ML, H - 10, MR, H - 10);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(140, 140, 140);
    doc.text(
      `Generated: ${new Date(report.generated_at).toLocaleString('en-IN')}   |   Sanjaya Admin Portal`,
      ML, H - 4.5
    );
    doc.text(`Page ${pageNum}`, MR, H - 4.5, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  };

  const origAddPage = doc.addPage.bind(doc);
  doc.addPage = (...args) => {
    if (!isCoverPage) drawFooter();
    const result = origAddPage(...args);
    pageNum++;
    isCoverPage = false;
    drawHeader();
    return result;
  };

  const ensureSpace = (needed = 20) => {
    if (y + needed > CONTENT_BOT) {
      doc.addPage();
      y = CONTENT_TOP;
    }
  };

  // ── COVER PAGE (page 1) ──────────────────────────────────────────────
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, H, 'F');
  doc.setFillColor(...ORANGE);
  doc.rect(0, H * 0.42, W, 2, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(40);
  doc.setFont('helvetica', 'bold');
  doc.text('SANJAYA', W / 2, 88, { align: 'center' });
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(190, 210, 230);
  doc.text('Child Development Monitoring Platform', W / 2, 100, { align: 'center' });
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('MONTHLY OPERATIONS REPORT', W / 2, 124, { align: 'center' });
  doc.setFontSize(28);
  doc.setTextColor(...ORANGE);
  doc.text(report.month_label.toUpperCase(), W / 2, 148, { align: 'center' });

  doc.setFillColor(28, 55, 85);
  doc.roundedRect(ML + 15, 165, CW - 30, 44, 2, 2, 'F');
  doc.setTextColor(170, 195, 220);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('PREPARED FOR', ML + 22, 178);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Sanjaya Management Team', ML + 22, 188);
  doc.setTextColor(170, 195, 220);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const genDate = new Date(report.generated_at).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  doc.text(`GENERATED: ${genDate}`, ML + 22, 198);
  doc.text('CLASSIFICATION: CONFIDENTIAL — INTERNAL USE ONLY', ML + 22, 207);
  doc.setTextColor(...ORANGE);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('CONFIDENTIAL — INTERNAL USE ONLY', W / 2, H - 18, { align: 'center' });

  // ── TABLE OF CONTENTS (page 2) ──────────────────────────────────────
  doc.addPage();
  y = CONTENT_TOP + 8;
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...NAVY);
  doc.text('TABLE OF CONTENTS', ML, y);
  y += 4;
  doc.setFillColor(...ORANGE);
  doc.rect(ML, y, CW, 0.8, 'F');
  y += 8;

  const tocItems = [
    'Executive Summary',
    'Child Development Overview',
    'Observer Performance',
    'Principal & School Overview',
    'Financial & Payment Overview',
    'Vendors & Third-Party Services',
    'Platform Usage & Engagement',
    'Alerts & Action Items',
    'Appendix',
  ];
  tocItems.forEach((title, i) => {
    if (i % 2 === 0) {
      doc.setFillColor(246, 248, 252);
      doc.rect(ML, y - 4, CW, 9, 'F');
    }
    doc.setFontSize(10.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(40, 40, 40);
    doc.text(`${i + 1}.  ${title}`, ML + 4, y);
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`${i + 3}`, MR - 2, y, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    y += 10;
  });

  // ── Section / sub-heading helpers ────────────────────────────────────
  let sectionNum = 0;
  const startSection = (title) => {
    sectionNum++;
    doc.addPage();
    y = CONTENT_TOP;
    doc.setFillColor(...NAVY);
    doc.rect(ML, y, CW, 11, 'F');
    doc.setFillColor(...ORANGE);
    doc.rect(ML, y + 11, CW, 0.8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`${sectionNum}.  ${title.toUpperCase()}`, ML + 4, y + 7.5);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    y += 16;
  };

  const subHeader = (text) => {
    ensureSpace(25);
    doc.setFillColor(...LIGHT_BLUE);
    doc.rect(ML, y, CW, 7, 'F');
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...NAVY);
    doc.text(text.toUpperCase(), ML + 3, y + 5);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    y += 10;
  };

  const kpiRow = (items) => {
    ensureSpace(25);
    const n = items.length;
    const bW = (CW - (n - 1) * 2) / n;
    items.forEach((item, i) => {
      const bx = ML + i * (bW + 2);
      doc.setDrawColor(...NAVY);
      doc.setLineWidth(0.3);
      doc.setFillColor(246, 248, 252);
      doc.roundedRect(bx, y, bW, 18, 1.5, 1.5, 'FD');
      doc.setFontSize(7);
      doc.setTextColor(100, 100, 100);
      doc.setFont('helvetica', 'normal');
      doc.text(String(item.label), bx + bW / 2, y + 5.5, { align: 'center' });
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...NAVY);
      doc.text(String(item.value), bx + bW / 2, y + 14, { align: 'center' });
    });
    doc.setLineWidth(0.2);
    doc.setTextColor(0, 0, 0);
    y += 22;
  };

  const aTable = (head, body, opts = {}) => {
    autoTable(doc, {
      startY: y,
      head,
      body,
      margin: { top: CONTENT_TOP, left: ML, right: W - MR, bottom: 14 },
      tableWidth: CW,
      headStyles: {
        fillColor: NAVY,
        textColor: [255, 255, 255],
        fontSize: 7.5,
        fontStyle: 'bold',
        cellPadding: { top: 2.5, bottom: 2.5, left: 3, right: 3 },
        halign: 'left',
      },
      bodyStyles: {
        fontSize: 7.5,
        cellPadding: { top: 2, bottom: 2, left: 3, right: 3 },
        textColor: [40, 40, 40],
      },
      alternateRowStyles: { fillColor: [246, 248, 252] },
      ...opts,
    });
    y = doc.lastAutoTable.finalY + 6;
    pageNum = doc.getNumberOfPages();
  };

  const {
    executive_summary: ex,
    child_overview: co,
    observer_performance: op,
    school_overview: so,
    financial: fi,
    vendor_costs: vc,
    platform_usage: pu,
    alerts: al,
    appendix: ap,
  } = report;

  // ── 1. Executive Summary ─────────────────────────────────────────────
  startSection('Executive Summary');
  kpiRow([
    { label: 'Active Children', value: fmtNum(ex.kpis.active_children) },
    { label: 'Sessions Conducted', value: fmtNum(ex.kpis.sessions_conducted) },
    { label: 'Gross Revenue', value: fmtRs(ex.kpis.gross_revenue) },
    { label: 'Net Revenue', value: fmtRs(ex.kpis.net_revenue) },
  ]);
  kpiRow([
    { label: 'Vendor Spend', value: fmtRs(ex.kpis.vendor_spend) },
    { label: 'Net Margin', value: `${ex.kpis.net_margin}%` },
    { label: 'Observer Payroll', value: fmtRs(op.payroll_summary.total_cost) },
    { label: 'Active Observers', value: fmtNum(op.kpis.active_observers) },
  ]);
  y += 2;
  if (ex.flags && ex.flags.length > 0) {
    subHeader('Key Flags This Month');
    ex.flags.forEach(f => {
      ensureSpace(14);
      const lines = doc.splitTextToSize(f, CW - 10);
      const bH = Math.max(10, lines.length * 4.5 + 6);
      doc.setFillColor(255, 240, 240);
      doc.rect(ML, y, CW, bH, 'F');
      doc.setFillColor(200, 50, 50);
      doc.rect(ML, y, 3, bH, 'F');
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(140, 30, 30);
      doc.text(lines, ML + 7, y + 5.5);
      doc.setTextColor(0, 0, 0);
      y += bH + 3;
    });
    y += 2;
  }
  aTable(
    [['Metric', 'Value']],
    [
      ['Period', report.month_label],
      ['Active Children', ex.kpis.active_children],
      ['Sessions Conducted', ex.kpis.sessions_conducted],
      ['Gross Revenue', fmtRs(ex.kpis.gross_revenue)],
      ['Total Vendor Spend', fmtRs(ex.kpis.vendor_spend)],
      ['Net Revenue', fmtRs(ex.kpis.net_revenue)],
      ['Net Margin', `${ex.kpis.net_margin}%`],
    ],
    { columnStyles: { 0: { cellWidth: 90 }, 1: { cellWidth: 'auto', fontStyle: 'bold' } } }
  );

  // ── 2. Child Development Overview ───────────────────────────────────
  startSection('Child Development Overview');
  kpiRow([
    { label: 'Active Children', value: fmtNum(co.kpis.active_children) },
    { label: 'Inactive', value: fmtNum(co.kpis.inactive_children) },
    { label: 'Report Completion', value: `${co.kpis.report_completion_pct}%` },
    { label: 'Avg Sessions / Child', value: co.kpis.avg_sessions_per_child },
  ]);
  subHeader('Age Group Breakdown');
  aTable(
    [['Age Group', 'Children', 'Avg Sessions', 'Reports Done', 'Completion %', 'Avg Fee (Rs.)']],
    (co.age_groups || []).map(r => [
      r.age_group, r.children, r.avg_sessions, r.reports_done,
      `${r.completion_pct}%`, fmtRs(r.avg_revenue_per_child),
    ]),
    { columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 20 }, 2: { cellWidth: 24 }, 3: { cellWidth: 24 }, 4: { cellWidth: 24 }, 5: { cellWidth: 'auto' } } }
  );
  if (co.risk_list && co.risk_list.length > 0) {
    subHeader('At-Risk Children (No Activity This Month)');
    aTable(
      [['Child', 'School', 'Observer', 'Last Session', 'Parent Contacted']],
      co.risk_list.map(r => [r.child_name, r.school, r.observer, r.last_session, r.parent_contacted]),
      { columnStyles: { 0: { cellWidth: 40 }, 1: { cellWidth: 38 }, 2: { cellWidth: 32 }, 3: { cellWidth: 28 }, 4: { cellWidth: 'auto' } } }
    );
  }

  // ── 3. Observer Performance ──────────────────────────────────────────
  startSection('Observer Performance');
  kpiRow([
    { label: 'Active Observers', value: fmtNum(op.kpis.active_observers) },
    { label: 'Inactive', value: fmtNum(op.kpis.inactive_observers) },
    { label: 'Avg Sessions', value: op.kpis.avg_sessions_per_observer },
    { label: 'Avg Payroll', value: fmtRs(op.kpis.avg_payroll) },
  ]);
  subHeader('Observer Breakdown');
  aTable(
    [['Observer', 'School', 'Sessions', 'Rpts Due', 'Done', 'Comp %', 'Calls', 'Payslip', 'Cost (Rs.)']],
    (op.observers || []).map(r => [
      r.observer, r.school, r.sessions, r.reports_due, r.reports_done,
      `${r.completion_pct}%`, r.calls_made, r.payslip_status, fmtRs(r.cost),
    ]),
    { columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 22 }, 2: { cellWidth: 16 }, 3: { cellWidth: 16 }, 4: { cellWidth: 14 }, 5: { cellWidth: 17 }, 6: { cellWidth: 14 }, 7: { cellWidth: 16 }, 8: { cellWidth: 'auto' } } }
  );
  subHeader('Payroll Summary');
  const ps = op.payroll_summary;
  aTable(
    [['Item', 'Count / Amount']],
    [
      ['Total Observers on Payroll', fmtNum(ps.total_on_payroll)],
      ['Paid Observers', fmtNum(ps.paid_count)],
      ['Amount Disbursed (Rs.)', fmtRs(ps.paid_amount)],
      ['Pending Observers', fmtNum(ps.pending_count)],
      ['Amount Pending (Rs.)', fmtRs(ps.pending_amount)],
      ['Total Payroll Cost (Rs.)', fmtRs(ps.total_cost)],
    ],
    {
      columnStyles: { 0: { cellWidth: 100 }, 1: { cellWidth: 'auto', halign: 'right', fontStyle: 'bold' } },
      didParseCell: (data) => {
        if (data.section === 'body' && (data.row.index === 2 || data.row.index === 5)) {
          data.cell.styles.fillColor = [225, 232, 240];
          data.cell.styles.textColor = [37, 75, 112];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    }
  );

  // ── 4. Principal & School Overview ───────────────────────────────────
  startSection('Principal & School Overview');
  aTable(
    [['School', 'Principal', 'Children', 'Observers', 'Sessions', 'Fee Collected', 'Outstanding', 'Collection %']],
    (so.schools || []).map(r => [
      r.school, r.principal, r.active_children, r.observers, r.sessions,
      fmtRs(r.fee_collected), fmtRs(r.outstanding_dues), `${r.collection_pct}%`,
    ]),
    { columnStyles: { 0: { cellWidth: 35 }, 1: { cellWidth: 24 }, 2: { cellWidth: 16 }, 3: { cellWidth: 16 }, 4: { cellWidth: 16 }, 5: { cellWidth: 28 }, 6: { cellWidth: 25 }, 7: { cellWidth: 'auto' } } }
  );

  // ── 5. Financial & Payment Overview ─────────────────────────────────
  startSection('Financial & Payment Overview');
  const pnl = fi.pnl;
  kpiRow([
    { label: 'Gross Revenue', value: fmtRs(pnl.gross_revenue) },
    { label: 'Total Expenses', value: fmtRs(pnl.less_observer_payroll + pnl.less_vendor_spend + pnl.less_misc) },
    { label: 'Operating Profit', value: fmtRs(pnl.operating_profit) },
    { label: 'Payroll % Revenue', value: `${pnl.payroll_pct_of_revenue}%` },
  ]);
  subHeader('Profit & Loss Statement');
  aTable(
    [['Item', 'Amount (Rs.)']],
    [
      ['Gross Revenue', fmtRs(pnl.gross_revenue)],
      ['Less: Refunds / Adjustments', fmtRs(pnl.less_refunds)],
      ['Net Revenue', fmtRs(pnl.net_revenue)],
      ['Less: Observer Payroll', fmtRs(pnl.less_observer_payroll)],
      ['Less: Vendor & Technology Spend', fmtRs(pnl.less_vendor_spend)],
      ['Less: Miscellaneous Expenses', fmtRs(pnl.less_misc)],
      ['Operating Profit', fmtRs(pnl.operating_profit)],
    ],
    {
      columnStyles: { 0: { cellWidth: 115 }, 1: { cellWidth: 'auto', halign: 'right' } },
      bodyStyles: { fontSize: 9 },
      didParseCell: (data) => {
        if (data.section === 'body' && (data.row.index === 2 || data.row.index === 6)) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [225, 232, 240];
          data.cell.styles.textColor = [37, 75, 112];
        }
      },
    }
  );

  // ── 6. Vendors & Third-Party Services ───────────────────────────────
  startSection('Vendors & Third-Party Services');
  const va = vc.analysis || {};
  kpiRow([
    { label: 'Total Vendor Spend', value: fmtRs(va.total_spend || 0) },
    { label: 'Largest Driver', value: va.largest_driver || '—' },
    { label: '% of Revenue', value: `${va.pct_of_revenue || 0}%` },
    { label: 'Active Vendors', value: (vc.vendors || []).filter(v => v.status === 'Active').length },
  ]);
  subHeader('Vendor Cost Breakdown');
  aTable(
    [['Vendor', 'Purpose', 'Plan / Usage', 'Current (Rs.)', 'Prev (Rs.)', 'Change %', 'Payment']],
    (vc.vendors || []).map(r => [
      r.vendor, r.purpose, r.plan_usage,
      fmtRs(r.cost_current), fmtRs(r.cost_prev),
      r.cost_prev > 0 ? `${r.change_pct}%` : '—',
      r.payment_status,
    ]),
    { columnStyles: { 0: { cellWidth: 28 }, 1: { cellWidth: 30 }, 2: { cellWidth: 24 }, 3: { cellWidth: 26 }, 4: { cellWidth: 24 }, 5: { cellWidth: 18 }, 6: { cellWidth: 'auto' } } }
  );

  // ── 7. Platform Usage & Engagement ──────────────────────────────────
  startSection('Platform Usage & Engagement');
  subHeader('Monthly Usage Metrics');
  aTable(
    [['Metric', 'Volume', 'Unit Cost (Rs.)', 'Total Cost (Rs.)', 'vs Last Month']],
    (pu.metrics || []).map(r => [r.metric, r.volume, r.unit_cost, r.total_cost, r.vs_last_month]),
    { columnStyles: { 0: { cellWidth: 65 }, 1: { cellWidth: 20 }, 2: { cellWidth: 28 }, 3: { cellWidth: 28 }, 4: { cellWidth: 'auto' } } }
  );

  // ── 8. Alerts & Action Items ─────────────────────────────────────────
  startSection('Alerts & Action Items');
  const alertPalette = {
    HIGH:   { text: [180, 30, 30],  bg: [255, 240, 240], bar: [200, 50, 50] },
    MEDIUM: { text: [150, 80, 0],   bg: [255, 248, 230], bar: [200, 130, 20] },
    LOW:    { text: [70, 70, 70],   bg: [248, 248, 248], bar: [140, 140, 140] },
    INFO:   { text: [37, 75, 112],  bg: [240, 246, 255], bar: [37, 75, 112] },
  };
  (al || []).forEach(alert => {
    const pal = alertPalette[alert.priority] || alertPalette.INFO;
    const lines = doc.splitTextToSize(alert.description, CW - 28);
    const boxH = Math.max(13, lines.length * 4.8 + 7);
    ensureSpace(boxH + 4);
    doc.setFillColor(...pal.bg);
    doc.rect(ML, y, CW, boxH, 'F');
    doc.setFillColor(...pal.bar);
    doc.rect(ML, y, 3, boxH, 'F');
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...pal.text);
    doc.text(alert.priority, ML + 6, y + 5.5);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(50, 50, 50);
    doc.text(lines, ML + 24, y + 5.5);
    doc.setTextColor(0, 0, 0);
    y += boxH + 3;
  });

  // ── 9. Appendix ──────────────────────────────────────────────────────
  startSection('Appendix');
  subHeader('Month-over-Month Performance Trend');
  aTable(
    [['Month', 'Gross Revenue (Rs.)', 'Observer Payroll (Rs.)', 'Active Children', 'Sessions']],
    (ap.mom_trend || []).map(r => [
      r.month, fmtRs(r.gross_revenue), fmtRs(r.observer_payroll),
      fmtNum(r.active_children), fmtNum(r.sessions),
    ]),
    { columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 42 }, 2: { cellWidth: 42 }, 3: { cellWidth: 28 }, 4: { cellWidth: 'auto' } } }
  );
  if (ap.top_observers && ap.top_observers.length > 0) {
    subHeader('Top Performing Observers');
    aTable(
      [['Observer', 'School', 'Sessions', 'Completion %', 'Payslip Status']],
      ap.top_observers.map(r => [
        r.observer, r.school || '—', r.sessions, `${r.completion_pct}%`, r.payslip_status,
      ]),
      { columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 35 }, 2: { cellWidth: 20 }, 3: { cellWidth: 25 }, 4: { cellWidth: 'auto' } } }
    );
  }
  if (ap.vendor_invoices && ap.vendor_invoices.length > 0) {
    subHeader('Vendor Invoice Register');
    aTable(
      [['Vendor', 'Invoice No.', 'Amount (Rs.)', 'Due Date', 'Status']],
      ap.vendor_invoices.map(r => [r.vendor, r.invoice_no, fmtRs(r.amount), r.due_date, r.status]),
      { columnStyles: { 0: { cellWidth: 45 }, 1: { cellWidth: 35 }, 2: { cellWidth: 30 }, 3: { cellWidth: 25 }, 4: { cellWidth: 'auto' } } }
    );
  }

  drawFooter();
  doc.save(`Sanjaya_Monthly_Report_${report.month}.pdf`);
}

function DashboardTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Monthly report state
  const defaultMonth = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMonth, setReportMonth] = useState(defaultMonth);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/ops-tracker/analytics/dashboard`, authH());
      setData(r.data);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load dashboard', variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const fetchMonthlyReport = async () => {
    setReportLoading(true);
    setReportData(null);
    try {
      const r = await axios.get(`${API_URL}/api/admin/monthly-report?month=${reportMonth}`, authH());
      setReportData(r.data);
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Could not generate report', variant: 'destructive' });
    } finally {
      setReportLoading(false);
    }
  };

  if (loading || !data) return <div className="p-8 text-center text-gray-500">Loading dashboard…</div>;

  const { kpis, sessions_trend, observer_breakdown, student_breakdown } = data;

  const kpiCards = [
    { label: 'Total Sessions', value: kpis.total_sessions, color: 'text-orange-600' },
    { label: 'Active Students', value: kpis.total_students_active, color: 'text-blue-600' },
    { label: 'Active Observers', value: kpis.total_observers_active, color: 'text-emerald-600' },
    { label: 'Active Subscribers', value: kpis.active_subscribers, color: 'text-purple-600' },
    { label: 'Feb Revenue', value: `₹${fmt(kpis.feb_revenue)}`, color: 'text-emerald-700' },
    { label: 'Feb Payout', value: `₹${fmt(kpis.total_payout)}`, color: 'text-amber-700' },
    { label: 'Net Margin', value: `₹${fmt(kpis.net_margin)}`, color: kpis.net_margin >= 0 ? 'text-emerald-700' : 'text-red-600' },
    { label: 'Open Queries', value: kpis.open_queries, color: 'text-red-600' },
  ];

  const fmtRs = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  return (
    <div className="space-y-4" data-testid="ops-dashboard">

      {/* Monthly Report button */}
      <div className="flex justify-end">
        <Button
          onClick={() => { setReportOpen(true); setReportData(null); }}
          className="bg-sanjaya-navy hover:bg-blue-900 text-white gap-2"
        >
          <FileBarChart2 className="w-4 h-4" />
          Monthly Report
        </Button>
      </div>

      {/* ===== Monthly Report Modal ===== */}
      {reportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 bg-[#254B70] rounded-t-xl">
              <div className="flex items-center gap-3">
                <FileBarChart2 className="w-5 h-5 text-white" />
                <h2 className="text-lg font-bold text-white">Monthly Report</h2>
              </div>
              <button onClick={() => setReportOpen(false)} className="text-white/70 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Month picker + Generate */}
            <div className="flex items-center gap-3 px-6 py-4 border-b bg-gray-50">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input
                type="month"
                value={reportMonth}
                onChange={e => { setReportMonth(e.target.value); setReportData(null); }}
                className="border rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#254B70]"
              />
              <Button
                onClick={fetchMonthlyReport}
                disabled={reportLoading}
                className="bg-[#F0A820] hover:bg-amber-500 text-white"
                size="sm"
              >
                {reportLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                {reportLoading ? 'Generating…' : 'Generate Report'}
              </Button>
              {reportData && (
                <Button
                  onClick={() => downloadMonthlyPDF(reportData)}
                  size="sm"
                  variant="outline"
                  className="border-[#254B70] text-[#254B70] hover:bg-blue-50 gap-1"
                >
                  <Download className="w-4 h-4" /> Download PDF
                </Button>
              )}
            </div>

            {/* Report Body */}
            <div className="overflow-y-auto flex-1 px-6 py-4 space-y-5">
              {!reportData && !reportLoading && (
                <div className="text-center py-12 text-gray-400">
                  <FileBarChart2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Select a month and click Generate Report</p>
                </div>
              )}
              {reportLoading && (
                <div className="text-center py-12 text-gray-400">
                  <Loader2 className="w-10 h-10 animate-spin mx-auto mb-3 text-[#254B70]" />
                  <p>Aggregating data…</p>
                </div>
              )}
              {reportData && (
                <>
                  {/* Cover */}
                  <div className="rounded-lg overflow-hidden border border-[#254B70]">
                    <div className="bg-[#254B70] px-6 py-6 text-center">
                      <p className="text-2xl font-bold text-white tracking-widest">SANJAYA</p>
                      <p className="text-xs text-blue-200 mt-1">Child Development Monitoring Platform</p>
                      <p className="text-sm font-semibold text-white mt-3 uppercase tracking-wider">Monthly Operations Report</p>
                      <p className="text-lg font-bold mt-1" style={{ color: '#F0A820' }}>{reportData.month_label.toUpperCase()}</p>
                    </div>
                    <div className="bg-gray-50 px-6 py-3 flex justify-between text-xs text-gray-500">
                      <span>Generated: {new Date(reportData.generated_at).toLocaleString('en-IN')}</span>
                      <span className="font-semibold text-orange-600">CONFIDENTIAL</span>
                    </div>
                  </div>

                  {/* 1. Executive Summary */}
                  <ReportSection title="1. Executive Summary">
                    <div className="grid grid-cols-2 gap-2 my-2">
                      {[
                        { l: 'Active Children', v: reportData.executive_summary.kpis.active_children },
                        { l: 'Sessions Conducted', v: reportData.executive_summary.kpis.sessions_conducted },
                        { l: 'Gross Revenue', v: fmtRs(reportData.executive_summary.kpis.gross_revenue) },
                        { l: 'Net Revenue', v: fmtRs(reportData.executive_summary.kpis.net_revenue) },
                        { l: 'Vendor Spend', v: fmtRs(reportData.executive_summary.kpis.vendor_spend) },
                        { l: 'Net Margin', v: `${reportData.executive_summary.kpis.net_margin}%` },
                        { l: 'Observer Payroll', v: fmtRs(reportData.observer_performance.payroll_summary.total_cost) },
                        { l: 'Active Observers', v: reportData.observer_performance.kpis.active_observers },
                      ].map(k => (
                        <div key={k.l} className="border border-gray-200 rounded px-3 py-2 bg-[#F7F9FC]">
                          <div className="text-xs text-gray-500">{k.l}</div>
                          <div className="text-sm font-bold text-[#254B70] mt-0.5">{k.v}</div>
                        </div>
                      ))}
                    </div>
                    {reportData.executive_summary.flags.map((f, i) => (
                      <div key={i} className="text-xs text-red-700 bg-red-50 border-l-2 border-red-400 px-3 py-1.5 rounded mb-1">{f}</div>
                    ))}
                  </ReportSection>

                  {/* 2. Child Development Overview */}
                  <ReportSection title="2. Child Development Overview">
                    <div className="grid grid-cols-4 gap-2 my-2">
                      {[
                        { l: 'Active', v: reportData.child_overview.kpis.active_children },
                        { l: 'Inactive', v: reportData.child_overview.kpis.inactive_children },
                        { l: 'Rpt Completion', v: `${reportData.child_overview.kpis.report_completion_pct}%` },
                        { l: 'Avg Sessions', v: reportData.child_overview.kpis.avg_sessions_per_child },
                      ].map(k => (
                        <div key={k.l} className="border border-gray-200 rounded px-2 py-1.5 bg-[#F7F9FC] text-center">
                          <div className="text-xs text-gray-500">{k.l}</div>
                          <div className="text-sm font-bold text-[#254B70]">{k.v}</div>
                        </div>
                      ))}
                    </div>
                    <ReportSubHead label="Age Group Breakdown" />
                    <ReportMiniTable
                      cols={['Age Group', 'Children', 'Avg Sessions', 'Completion %', 'Avg Fee']}
                      rows={(reportData.child_overview.age_groups || []).map(r => [
                        r.age_group, r.children, r.avg_sessions, `${r.completion_pct}%`, fmtRs(r.avg_revenue_per_child),
                      ])}
                    />
                    {reportData.child_overview.risk_list.length > 0 && (
                      <>
                        <ReportSubHead label="At-Risk Children" />
                        <ReportMiniTable
                          cols={['Child', 'School', 'Observer', 'Parent Contacted']}
                          rows={reportData.child_overview.risk_list.map(r => [r.child_name, r.school, r.observer, r.parent_contacted])}
                        />
                      </>
                    )}
                  </ReportSection>

                  {/* 3. Observer Performance */}
                  <ReportSection title="3. Observer Performance">
                    <div className="grid grid-cols-4 gap-2 my-2">
                      {[
                        { l: 'Active', v: reportData.observer_performance.kpis.active_observers },
                        { l: 'Inactive', v: reportData.observer_performance.kpis.inactive_observers },
                        { l: 'Avg Sessions', v: reportData.observer_performance.kpis.avg_sessions_per_observer },
                        { l: 'Avg Payroll', v: fmtRs(reportData.observer_performance.kpis.avg_payroll) },
                      ].map(k => (
                        <div key={k.l} className="border border-gray-200 rounded px-2 py-1.5 bg-[#F7F9FC] text-center">
                          <div className="text-xs text-gray-500">{k.l}</div>
                          <div className="text-sm font-bold text-[#254B70]">{k.v}</div>
                        </div>
                      ))}
                    </div>
                    <ReportSubHead label="Observer Breakdown" />
                    <ReportMiniTable
                      cols={['Observer', 'Sessions', 'Comp %', 'Payslip', 'Cost (Rs.)']}
                      rows={(reportData.observer_performance.observers || []).map(r => [
                        r.observer, r.sessions, `${r.completion_pct}%`, r.payslip_status, fmtRs(r.cost),
                      ])}
                    />
                    <ReportSubHead label="Payroll Summary" />
                    {(() => {
                      const p = reportData.observer_performance.payroll_summary;
                      return (
                        <>
                          <ReportRow label="Total Observers on Payroll" value={p.total_on_payroll} />
                          <ReportRow label="Paid Observers" value={p.paid_count} />
                          <ReportRow label="Amount Disbursed" value={fmtRs(p.paid_amount)} />
                          <ReportRow label="Pending Observers" value={p.pending_count} />
                          <ReportRow label="Amount Pending" value={fmtRs(p.pending_amount)} />
                          <ReportTotalRow label="Total Payroll Cost" value={fmtRs(p.total_cost)} />
                        </>
                      );
                    })()}
                  </ReportSection>

                  {/* 4. Principal & School Overview */}
                  <ReportSection title="4. Principal & School Overview">
                    <ReportMiniTable
                      cols={['School', 'Children', 'Sessions', 'Fee Collected', 'Outstanding', 'Collection %']}
                      rows={(reportData.school_overview.schools || []).map(r => [
                        r.school, r.active_children, r.sessions,
                        fmtRs(r.fee_collected), fmtRs(r.outstanding_dues), `${r.collection_pct}%`,
                      ])}
                    />
                  </ReportSection>

                  {/* 5. Financial & Payment Overview */}
                  <ReportSection title="5. Financial & Payment Overview">
                    {(() => {
                      const p = reportData.financial.pnl;
                      return (
                        <>
                          <ReportRow label="Gross Revenue" value={fmtRs(p.gross_revenue)} />
                          <ReportRow label="Less: Refunds" value={fmtRs(p.less_refunds)} indent />
                          <ReportTotalRow label="Net Revenue" value={fmtRs(p.net_revenue)} />
                          <ReportRow label="Less: Observer Payroll" value={fmtRs(p.less_observer_payroll)} indent />
                          <ReportRow label="Less: Vendor Spend" value={fmtRs(p.less_vendor_spend)} indent />
                          <ReportRow label="Less: Miscellaneous" value={fmtRs(p.less_misc)} indent />
                          <ReportTotalRow label="Operating Profit" value={fmtRs(p.operating_profit)} />
                          <div className="text-xs text-gray-500 mt-1">Payroll as % of Revenue: {p.payroll_pct_of_revenue}%</div>
                        </>
                      );
                    })()}
                  </ReportSection>

                  {/* 6. Vendors & Third-Party Services */}
                  <ReportSection title="6. Vendors & Third-Party Services">
                    <div className="grid grid-cols-3 gap-2 my-2">
                      {[
                        { l: 'Total Vendor Spend', v: fmtRs(reportData.vendor_costs.analysis?.total_spend || 0) },
                        { l: 'Largest Driver', v: reportData.vendor_costs.analysis?.largest_driver || '—' },
                        { l: '% of Revenue', v: `${reportData.vendor_costs.analysis?.pct_of_revenue || 0}%` },
                      ].map(k => (
                        <div key={k.l} className="border border-gray-200 rounded px-2 py-1.5 bg-[#F7F9FC] text-center">
                          <div className="text-xs text-gray-500">{k.l}</div>
                          <div className="text-sm font-bold text-[#254B70]">{k.v}</div>
                        </div>
                      ))}
                    </div>
                    <ReportMiniTable
                      cols={['Vendor', 'Purpose', 'Current (Rs.)', 'Prev (Rs.)', 'Payment']}
                      rows={(reportData.vendor_costs.vendors || []).map(r => [
                        r.vendor, r.purpose, fmtRs(r.cost_current), fmtRs(r.cost_prev), r.payment_status,
                      ])}
                    />
                  </ReportSection>

                  {/* 7. Platform Usage */}
                  <ReportSection title="7. Platform Usage & Engagement">
                    <ReportMiniTable
                      cols={['Metric', 'Volume', 'Total Cost (Rs.)', 'vs Last Month']}
                      rows={(reportData.platform_usage.metrics || []).map(r => [
                        r.metric, r.volume, r.total_cost, r.vs_last_month,
                      ])}
                    />
                  </ReportSection>

                  {/* 8. Alerts */}
                  <ReportSection title="8. Alerts & Action Items">
                    {(reportData.alerts || []).map((a, i) => {
                      const cls = {
                        HIGH:   'bg-red-50 border-red-400 text-red-800',
                        MEDIUM: 'bg-amber-50 border-amber-400 text-amber-800',
                        LOW:    'bg-gray-50 border-gray-300 text-gray-700',
                        INFO:   'bg-blue-50 border-blue-300 text-blue-800',
                      }[a.priority] || 'bg-gray-50 border-gray-300 text-gray-700';
                      return (
                        <div key={i} className={`border-l-4 px-3 py-2 rounded mb-1.5 text-xs ${cls}`}>
                          <span className="font-bold uppercase mr-2">{a.priority}</span>
                          {a.description}
                        </div>
                      );
                    })}
                  </ReportSection>

                  {/* 9. Appendix */}
                  <ReportSection title="9. Appendix">
                    <ReportSubHead label="Month-over-Month Trend" />
                    <ReportMiniTable
                      cols={['Month', 'Gross Revenue', 'Observer Payroll', 'Children', 'Sessions']}
                      rows={(reportData.appendix.mom_trend || []).map(r => [
                        r.month, fmtRs(r.gross_revenue), fmtRs(r.observer_payroll),
                        r.active_children, r.sessions,
                      ])}
                    />
                    {reportData.appendix.top_observers.length > 0 && (
                      <>
                        <ReportSubHead label="Top Performing Observers" />
                        <ReportMiniTable
                          cols={['Observer', 'Sessions', 'Completion %', 'Payslip']}
                          rows={reportData.appendix.top_observers.map(r => [
                            r.observer, r.sessions, `${r.completion_pct}%`, r.payslip_status,
                          ])}
                        />
                      </>
                    )}
                  </ReportSection>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpiCards.map(k => (
          <Card key={k.label} data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <CardContent className="p-4">
              <div className="text-xs text-gray-500 uppercase tracking-wide">{k.label}</div>
              <div className={`text-2xl font-bold mt-1 ${k.color}`}>{k.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Sessions Conducted — Trend</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={sessions_trend}>
                <defs>
                  <linearGradient id="cf" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f97316" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Area type="monotone" dataKey="sessions" stroke="#f97316" fill="url(#cf)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Sessions by Observer</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={observer_breakdown}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="observer" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="sessions" fill="#0ea5e9" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Sessions by Student</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={student_breakdown} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="student" type="category" width={100} />
                <Tooltip />
                <Bar dataKey="sessions" fill="#10b981" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Revenue vs Payout (Feb)</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Revenue', value: kpis.feb_revenue },
                    { name: 'Payout', value: kpis.total_payout },
                  ]}
                  dataKey="value"
                  innerRadius={60}
                  outerRadius={100}
                  label={({ name, value }) => `${name}: ₹${fmt(value)}`}
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#f59e0b" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ===================== Collection (CRUD) tab =====================

function CollectionTab({ tab }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id of row being edited
  const [draft, setDraft] = useState({});
  const [search, setSearch] = useState('');
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/ops-tracker/${tab.id}`, authH());
      setRows(r.data.items || []);
    } catch (e) {
      toast({ title: 'Error', description: 'Could not load rows', variant: 'destructive' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab.id]);

  const startNew = () => {
    setEditing('__new__');
    setDraft({});
  };
  const startEdit = (row) => {
    setEditing(row.id);
    setDraft({ ...row });
  };
  const cancelEdit = () => { setEditing(null); setDraft({}); };

  const saveRow = async () => {
    try {
      // Coerce numeric fields
      const payload = { ...draft };
      tab.columns.forEach(c => {
        if (c.type === 'number' && payload[c.key] !== '' && payload[c.key] !== undefined && payload[c.key] !== null) {
          const n = Number(payload[c.key]);
          payload[c.key] = isNaN(n) ? payload[c.key] : n;
        }
      });
      if (editing === '__new__') {
        await axios.post(`${API_URL}/api/ops-tracker/${tab.id}`, payload, authH());
        toast({ title: 'Added', description: 'Row created.' });
      } else {
        await axios.put(`${API_URL}/api/ops-tracker/${tab.id}/${editing}`, payload, authH());
        toast({ title: 'Saved', description: 'Row updated.' });
      }
      cancelEdit();
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm('Delete this row? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/ops-tracker/${tab.id}/${id}`, authH());
      toast({ title: 'Deleted', description: 'Row removed.' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed', variant: 'destructive' });
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      tab.columns.some(c => String(r[c.key] ?? '').toLowerCase().includes(q))
    );
  }, [rows, search, tab.columns]);

  const downloadCSV = () => {
    const headers = tab.columns.map(c => c.label);
    const lines = [headers.join(',')];
    filtered.forEach(r => {
      lines.push(tab.columns.map(c => {
        const v = r[c.key];
        if (v === null || v === undefined) return '';
        const s = String(v).replace(/"/g, '""');
        return /[,"\n]/.test(s) ? `"${s}"` : s;
      }).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${tab.id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div>
            <CardTitle className="text-base">{tab.label}</CardTitle>
            <CardDescription className="text-xs">{tab.description}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Search..." className="pl-8 h-9 w-48" value={search} onChange={e => setSearch(e.target.value)} data-testid={`search-${tab.id}`} />
            </div>
            <Button variant="outline" size="sm" onClick={load} data-testid={`refresh-${tab.id}`}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={downloadCSV} data-testid={`export-${tab.id}`}>
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
            <Button size="sm" onClick={startNew} className="bg-orange-500 hover:bg-orange-600" data-testid={`add-row-${tab.id}`}>
              <Plus className="w-4 h-4 mr-1" /> Add Row
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-0 pb-0">
          {loading ? (
            <div className="p-6 text-center text-gray-500">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-y">
                  <tr>
                    {tab.columns.map(c => (
                      <th key={c.key} className="px-3 py-2 text-left font-medium text-gray-600" style={c.width ? { width: c.width } : null}>
                        {c.label}
                      </th>
                    ))}
                    <th className="px-3 py-2 w-28 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {editing === '__new__' && (
                    <EditRow tab={tab} draft={draft} setDraft={setDraft} onSave={saveRow} onCancel={cancelEdit} />
                  )}
                  {filtered.length === 0 && editing !== '__new__' ? (
                    <tr><td colSpan={tab.columns.length + 1} className="px-3 py-8 text-center text-gray-400">No rows yet. Click "Add Row" to create one.</td></tr>
                  ) : (
                    filtered.map(r => (
                      editing === r.id ? (
                        <EditRow key={r.id} tab={tab} draft={draft} setDraft={setDraft} onSave={saveRow} onCancel={cancelEdit} />
                      ) : (
                        <tr key={r.id} className="border-b hover:bg-gray-50">
                          {tab.columns.map(c => (
                            <td key={c.key} className="px-3 py-2 text-gray-800 align-top">{formatCell(r[c.key])}</td>
                          ))}
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <Button variant="ghost" size="sm" onClick={() => startEdit(r)} data-testid={`edit-${tab.id}-${r.id}`}>Edit</Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteRow(r.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-${tab.id}-${r.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      )
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Charts per tab */}
      <TabChart tabId={tab.chart} rows={rows} />
    </div>
  );
}

function EditRow({ tab, draft, setDraft, onSave, onCancel }) {
  return (
    <tr className="border-b bg-orange-50">
      {tab.columns.map(c => (
        <td key={c.key} className="px-2 py-2">
          {c.options ? (
            <select
              className="w-full border rounded px-2 py-1 text-sm bg-white"
              value={draft[c.key] ?? ''}
              onChange={e => setDraft({ ...draft, [c.key]: e.target.value })}
              data-testid={`field-${tab.id}-${c.key}`}
            >
              <option value="">—</option>
              {c.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <Input
              className="h-8 text-sm"
              type={c.type === 'number' ? 'number' : 'text'}
              value={draft[c.key] ?? ''}
              onChange={e => setDraft({ ...draft, [c.key]: e.target.value })}
              data-testid={`field-${tab.id}-${c.key}`}
            />
          )}
        </td>
      ))}
      <td className="px-3 py-2 text-right whitespace-nowrap">
        <Button size="sm" onClick={onSave} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`save-row-${tab.id}`}>
          <Save className="w-4 h-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid={`cancel-row-${tab.id}`}>
          <X className="w-4 h-4" />
        </Button>
      </td>
    </tr>
  );
}

// ===================== Charts per tab =====================

function TabChart({ tabId, rows }) {
  // Build the right chart per tab from the in-memory rows
  switch (tabId) {
    case 'daily-log': {
      const counts = groupBy(rows, 'session_status');
      const data = Object.entries(counts).map(([k, v]) => ({ status: k || 'Unknown', count: v }));
      return (
        <ChartCard title="Sessions by Status">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="status" /><YAxis /><Tooltip />
            <Bar dataKey="count" fill="#f97316" />
          </BarChart>
        </ChartCard>
      );
    }
    case 'subscribers': {
      const data = rows.map(r => ({
        name: r.child_name,
        Feb: num(r.feb_revenue), Mar: num(r.mar_revenue), Apr: num(r.apr_revenue),
      }));
      return (
        <ChartCard title="Revenue per Subscriber (₹)">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" /><YAxis /><Tooltip /><Legend />
            <Bar dataKey="Feb" stackId="a" fill="#10b981" />
            <Bar dataKey="Mar" stackId="a" fill="#0ea5e9" />
            <Bar dataKey="Apr" stackId="a" fill="#a855f7" />
          </BarChart>
        </ChartCard>
      );
    }
    case 'session-schedule': {
      const data = rows.map(r => ({ name: r.child_name, sessions: num(r.sessions_feb) }));
      return (
        <ChartCard title="Planned Sessions per Child">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" /><YAxis /><Tooltip />
            <Bar dataKey="sessions" fill="#0ea5e9" />
          </BarChart>
        </ChartCard>
      );
    }
    case 'session-duration': {
      const data = rows
        .filter(r => r.actual_duration || r.scheduled_duration)
        .map((r, i) => ({
          idx: `${r.date || ''} ${r.student || ''}`.trim() || `#${i + 1}`,
          Scheduled: num(r.scheduled_duration),
          Actual: num(r.actual_duration),
        }));
      return (
        <ChartCard title="Scheduled vs Actual Duration (min)">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="idx" hide /><YAxis /><Tooltip /><Legend />
            <Line type="monotone" dataKey="Scheduled" stroke="#0ea5e9" />
            <Line type="monotone" dataKey="Actual" stroke="#f97316" />
          </LineChart>
        </ChartCard>
      );
    }
    case 'missed-sessions': {
      const counts = groupBy(rows, 'reason_observer');
      const data = Object.entries(counts).map(([k, v]) => ({ reason: k || 'Unknown', count: v }));
      return (
        <ChartCard title="Missed Sessions by Reason">
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="reason" outerRadius={90} label>
              {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ChartCard>
      );
    }
    case 'postponements': {
      const counts = groupBy(rows, 'postponed_by');
      const data = Object.entries(counts).map(([k, v]) => ({ by: k || 'Unknown', count: v }));
      return (
        <ChartCard title="Postponements by Party">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="by" /><YAxis /><Tooltip />
            <Bar dataKey="count" fill="#a855f7" />
          </BarChart>
        </ChartCard>
      );
    }
    case 'queries': {
      const counts = groupBy(rows, 'status');
      const data = Object.entries(counts).map(([k, v]) => ({ status: k || 'Unknown', count: v }));
      return (
        <ChartCard title="Queries by Status">
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="status" outerRadius={90} label>
              {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ChartCard>
      );
    }
    case 'parent-forms': {
      const counts = groupBy(rows, 'gender');
      const data = Object.entries(counts).map(([k, v]) => ({ gender: k || 'Unknown', count: v }));
      return (
        <ChartCard title="Inquiries by Gender">
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="gender" outerRadius={90} label>
              {data.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ChartCard>
      );
    }
    case 'observer-payroll': {
      const data = rows.map(r => ({ name: r.observer_name, pay: num(r.total_pay) }));
      return (
        <ChartCard title="Total Pay per Observer (Rs.)">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" /><YAxis /><Tooltip />
            <Bar dataKey="pay" fill="#10b981" />
          </BarChart>
        </ChartCard>
      );
    }
    case 'vendor-costs': {
      const data = rows.map(r => ({
        name: r.vendor_name || '—',
        current: num(r.cost_current),
        prev: num(r.cost_prev),
      }));
      return (
        <ChartCard title="Vendor Cost — Current vs Previous Month (Rs.)">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" /><YAxis /><Tooltip /><Legend />
            <Bar dataKey="current" name="Current Month" fill="#254B70" />
            <Bar dataKey="prev" name="Prev Month" fill="#F0A820" />
          </BarChart>
        </ChartCard>
      );
    }
    case 'platform-usage': {
      const data = rows.map(r => ({
        metric: (r.metric || '').replace('Total Logins — ', '').replace('Total ', ''),
        total_cost: num(r.total_cost),
        volume: num(r.volume),
      }));
      return (
        <ChartCard title="Platform Usage — Cost by Metric (Rs.)">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="metric" /><YAxis /><Tooltip />
            <Bar dataKey="total_cost" name="Total Cost (Rs.)" fill="#0ea5e9" />
          </BarChart>
        </ChartCard>
      );
    }
    default:
      return null;
  }
}

// ---- Monthly Report sub-components ----

function ReportSection({ title, children }) {
  return (
    <div className="rounded border border-gray-200 overflow-hidden">
      <div className="bg-[#254B70] px-4 py-2">
        <h3 className="font-bold text-xs uppercase tracking-widest text-white">{title}</h3>
      </div>
      <div className="bg-white px-4 py-2 space-y-0.5">{children}</div>
    </div>
  );
}

function ReportSubHead({ label }) {
  return (
    <div className="text-xs font-bold uppercase tracking-wider text-[#254B70] bg-[#EBF0F6] px-2 py-1 -mx-2 mt-2 mb-1">
      {label}
    </div>
  );
}

function ReportRow({ label, value, indent }) {
  return (
    <div className={`flex justify-between items-center text-sm py-1 border-b border-gray-50 last:border-0 ${indent ? 'pl-4' : ''}`}>
      <span className="text-gray-600">{label}</span>
      <span className="font-semibold text-gray-800 tabular-nums">{value}</span>
    </div>
  );
}

function ReportTotalRow({ label, value }) {
  return (
    <div className="flex justify-between items-center text-sm font-bold bg-[#254B70] text-white px-2 py-1.5 -mx-2 mt-1">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function ReportMiniTable({ cols, rows }) {
  return (
    <div className="overflow-x-auto -mx-4 mb-1">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="bg-[#254B70] text-white">
            {cols.map(c => <th key={c} className="px-3 py-1.5 text-left font-semibold whitespace-nowrap">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0
            ? <tr><td colSpan={cols.length} className="px-3 py-4 text-center text-gray-400">No data</td></tr>
            : rows.map((row, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F7F9FC]'}>
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-1.5 text-gray-700 whitespace-nowrap">{cell ?? '—'}</td>
                ))}
              </tr>
            ))
          }
        </tbody>
      </table>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={280}>{children}</ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ===================== Analytics (read-only) tabs =====================

function AnalyticsTab({ tab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/ops-tracker/analytics/${tab.endpoint}`, authH());
        setData(r.data);
      } catch (e) {
        toast({ title: 'Error', description: 'Could not load', variant: 'destructive' });
      } finally { setLoading(false); }
    })();
  }, [tab.id]);

  if (loading || !data) return <div className="p-8 text-center text-gray-500">Loading…</div>;

  if (tab.id === 'observer-tracker') {
    return <ObserverTrackerView data={data} />;
  }
  if (tab.id === 'student-tracker') {
    return <StudentTrackerView data={data} />;
  }
  if (tab.id === 'pnl') {
    return <PnLView data={data} />;
  }
  return null;
}

function ObserverTrackerView({ data }) {
  const cols = [
    { key: 'observer', label: 'Observer' }, { key: 'children_assigned', label: 'Children' },
    { key: 'completed_sessions', label: 'Done' }, { key: 'missed_sessions', label: 'Missed' },
    { key: 'postponed_sessions', label: 'Postponed' }, { key: 'attendance_pct', label: 'Attendance %' },
    { key: 'total_minutes', label: 'Total Min' }, { key: 'avg_minutes', label: 'Avg Min/Session' },
    { key: 'total_pay', label: 'Pay (₹)' },
  ];
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Observer Tracker (auto-calculated)</CardTitle>
          <CardDescription className="text-xs">Edits to Daily Log automatically reflect here.</CardDescription>
        </CardHeader>
        <CardContent className="px-0"><RollupTable cols={cols} rows={data.items} /></CardContent>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Attendance % by Observer">
          <BarChart data={data.items}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="observer" /><YAxis domain={[0, 100]} /><Tooltip />
            <Bar dataKey="attendance_pct" fill="#10b981" />
          </BarChart>
        </ChartCard>
        <ChartCard title="Sessions Completed vs Missed">
          <BarChart data={data.items}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="observer" /><YAxis /><Tooltip /><Legend />
            <Bar dataKey="completed_sessions" fill="#0ea5e9" name="Completed" />
            <Bar dataKey="missed_sessions" fill="#ef4444" name="Missed" />
          </BarChart>
        </ChartCard>
      </div>
    </div>
  );
}

function StudentTrackerView({ data }) {
  const cols = [
    { key: 'student', label: 'Student' }, { key: 'observer', label: 'Observer' },
    { key: 'completed_sessions', label: 'Done' }, { key: 'missed_sessions', label: 'Missed' },
    { key: 'postponed_sessions', label: 'Postponed' }, { key: 'attendance_pct', label: 'Attendance %' },
    { key: 'total_minutes', label: 'Total Min' }, { key: 'avg_minutes', label: 'Avg Min/Session' },
  ];
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle className="text-base">Student Tracker (auto-calculated)</CardTitle></CardHeader>
        <CardContent className="px-0"><RollupTable cols={cols} rows={data.items} /></CardContent>
      </Card>
      <ChartCard title="Sessions Completed per Student">
        <BarChart data={data.items}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="student" /><YAxis /><Tooltip />
          <Bar dataKey="completed_sessions" fill="#a855f7" />
        </BarChart>
      </ChartCard>
    </div>
  );
}

function PnLView({ data }) {
  const monthly = [
    { month: 'Feb', Revenue: data.monthly.feb.revenue, Payout: data.monthly.feb.payout, Net: data.monthly.feb.net },
    { month: 'Mar', Revenue: data.monthly.mar.revenue, Payout: data.monthly.mar.payout, Net: data.monthly.mar.net },
    { month: 'Apr', Revenue: data.monthly.apr.revenue, Payout: data.monthly.apr.payout, Net: data.monthly.apr.net },
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs text-gray-500 uppercase">Total Revenue</div>
          <div className="text-2xl font-bold text-emerald-700 mt-1">₹{fmt(data.totals.revenue)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-gray-500 uppercase">Total Payout</div>
          <div className="text-2xl font-bold text-amber-700 mt-1">₹{fmt(data.totals.payout)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-gray-500 uppercase">Net</div>
          <div className={`text-2xl font-bold mt-1 ${data.totals.net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>₹{fmt(data.totals.net)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs text-gray-500 uppercase">Subscribers</div>
          <div className="text-2xl font-bold text-purple-700 mt-1">{data.revenue_by_subscriber.length}</div>
        </CardContent></Card>
      </div>
      <ChartCard title="Monthly Revenue, Payout & Net (₹)">
        <BarChart data={monthly}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" /><YAxis /><Tooltip /><Legend />
          <Bar dataKey="Revenue" fill="#10b981" />
          <Bar dataKey="Payout" fill="#f59e0b" />
          <Bar dataKey="Net" fill="#0ea5e9" />
        </BarChart>
      </ChartCard>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Revenue by Subscriber (Feb)">
          <BarChart data={data.revenue_by_subscriber}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" /><YAxis /><Tooltip />
            <Bar dataKey="feb" fill="#10b981" />
          </BarChart>
        </ChartCard>
        <ChartCard title="Payout by Observer">
          <BarChart data={data.payout_by_observer}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" /><YAxis /><Tooltip />
            <Bar dataKey="total_pay" fill="#f59e0b" />
          </BarChart>
        </ChartCard>
      </div>
    </div>
  );
}

function RollupTable({ cols, rows }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 border-y">
          <tr>{cols.map(c => <th key={c.key} className="px-3 py-2 text-left font-medium text-gray-600">{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b hover:bg-gray-50">
              {cols.map(c => <td key={c.key} className="px-3 py-2 text-gray-800">{formatCell(r[c.key])}</td>)}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-gray-400">No data yet.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ===================== Helpers =====================

function authH() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } };
}

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return '0';
  return Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function formatCell(v) {
  if (v === null || v === undefined || v === '') return <span className="text-gray-300">—</span>;
  if (typeof v === 'number') return v.toLocaleString('en-IN');
  return String(v);
}

function groupBy(rows, key) {
  const out = {};
  rows.forEach(r => { const k = r[key] ?? 'Unknown'; out[k] = (out[k] || 0) + 1; });
  return out;
}

function num(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}
